<script setup lang="ts">
import { useAppStore } from '@/store/app'
import { ChevronLeftIcon } from '@gohighlevel/ghl-icons/24/outline'
import {
  UIButton,
  UITextSmMedium,
  UIToolbar,
  UIToolbarGroup,
  UIInput,
  UITag,
} from '@gohighlevel/ghl-ui'
import { computed } from 'vue'
import DocumentActions from './ValidateDocument/DocumentActions.vue'
import config from '@/config'
import { DocumentStatus } from '@gohighlevel/ghl-proposals-common'
import { useBuilderStore } from '@/store/builder'
import DocumentStateActions from './ValidateDocument/DocumentStateActions.vue'
import { useDocument } from '@/composition'
import { useRoute } from 'vue-router'

const route = useRoute()
const { saveDocument } = useDocument()

const emit = defineEmits<{
  (e: 'onBack'): void
}>()
function goBack() {
  emit('onBack')
}

const appStore = useAppStore()
const builderStore = useBuilderStore()
const documentTitle = computed({
  get() {
    return appStore.document?.name
  },
  set(val: string) {
    appStore.document.name = val
  },
})

function listenForTitleChanges(val: string) {
  appStore.updateDocumentName(val)
}

const whiteLabelBaseUrl = computed(() => {
  return appStore?.whiteLabelViewerBaseUrl
})

const getBaseURL = () => {
  if (
    process.env.NODE_ENV === 'development' ||
    process.env.NODE_ENV === 'beta'
  ) {
    return config.DOCUMENT_VIEWER_URL
  }
  return whiteLabelBaseUrl.value || config.DOCUMENT_VIEWER_URL
}
const openPreviewLink = async (event: Event) => {
  event.preventDefault()
  if (appStore.document.status === DocumentStatus.DRAFT) {
    await saveDocument()
  }
  const baseUrl = getBaseURL()
  const newTab = window.open(
    `${baseUrl}/documents/preview/${route.params.documentId}?locale=${appStore.locale}`,
    '_blank'
  )
  newTab?.focus()
}

const isDocumentStatusExpired = (document: any) => {
  if (document.isExpired) return true
  return false
}
const getStatusTypeAndLabel = (
  status: string | undefined
): {
  type: 'default' | 'error' | 'primary' | 'success' | 'warning' | 'info'
  label: string
} => {
  if (!status) {
    return { type: 'default', label: 'common.draft' }
  }
  if (status === DocumentStatus.DRAFT) {
    return { type: 'default', label: 'common.draft' }
  }
  if (
    status !== DocumentStatus.COMPLETED &&
    status !== DocumentStatus.DECLINED &&
    isDocumentStatusExpired(appStore.document)
  ) {
    return { type: 'warning', label: 'common.expired' }
  }
  if (status === DocumentStatus.VIEWED) {
    return { type: 'success', label: 'common.viewed' }
  }
  if (status === DocumentStatus.SENT) {
    return { type: 'primary', label: 'proposals.sendLink.sent' }
  }
  if (status === DocumentStatus.COMPLETED) {
    return { type: 'success', label: 'common.completed' }
  }
  if (status === DocumentStatus.ACCEPTED) {
    return { type: 'success', label: 'common.accepted' }
  }
  if (status === DocumentStatus.WAITING_FOR_PAYMENT) {
    return { type: 'warning', label: 'common.paymentPending' }
  }
  if (status === DocumentStatus.DECLINED) {
    return { type: 'error', label: 'common.declined' }
  }
  return { type: 'default', label: 'common.draft' }
}
</script>

<template>
  <UIToolbar theme="light">
    <div class="w-1/12 flex flex-1 justify-start">
      <div class="inline-flex">
        <UIToolbarGroup border-right class="flex items-center">
          <UIButton id="btn-1" text @click="goBack">
            <ChevronLeftIcon class="w-5 h-5 text-gray-700" />
            <UITextSmMedium class="text-gray-700 font-medium">
              {{ $t('common.back') }}
            </UITextSmMedium>
          </UIButton>
        </UIToolbarGroup>
      </div>
    </div>
    <div class="w-6/12 md:w-3/12 flex flex-1 justify-center">
      <UIToolbarGroup class="inline-flex justify-center items-center w-full">
        <UIInput
          id="document-title"
          :class="`w-full text-lg text-gray-700 max-w-md border px-3 rounded py-1 hover:bg-gray-100 cursor-pointer overflow-x-auto text-center whitespace-nowrap shadow-none ${
            !builderStore.isDocumentEditable
              ? 'pointer-events-none opacity-50'
              : ''
          }`"
          v-model="documentTitle"
          @update:model-value="val => listenForTitleChanges(val)"
        />
      </UIToolbarGroup>
    </div>
    <div class="w-3/12 md:w-4/12 flex flex-1 justify-end">
      <UIToolbarGroup class="inline-flex justify-end items-center">
        <div class="flex items-center w-full">
          <UITag
            :type="getStatusTypeAndLabel(appStore.document?.status).type"
            size="small"
            class="capitalize whitespace-nowrap mr-3"
          >
            {{ $t(getStatusTypeAndLabel(appStore.document?.status).label) }}
          </UITag>
          <UISpace class="flex items-center space-x-3">
            <DocumentStateActions />
          </UISpace>
          <a
            class="no-underline text-primary-700 font-medium ml-3 mr-6"
            href="javascript:void(0)"
            @click="openPreviewLink"
          >
            {{
              appStore.document.status !== DocumentStatus.DRAFT
                ? $t('common.view')
                : $t('common.preview')
            }}
          </a>
          <UISpace class="flex items-center space-x-3">
            <DocumentActions />
          </UISpace>
        </div>
      </UIToolbarGroup>
    </div>
  </UIToolbar>
</template>

<style lang="scss">
#document-title {
  border: 1px;
  input {
    height: 28px;
    line-height: 28px;
  }
}
</style>
